// src/features/bots/botsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../axios';

/** ====== ТИПЫ ====== */
interface ProfitStats {
  today: number;
  sinceStart: number;
  allTime: number;
}

interface Bot {
  _id: string;
  binanceAccountId: string;
  symbol: string;
  entryAmount: number;
  status: string;
  fee?: number;
  autoTuneEnabled?: boolean;
  position?: string | null; // 'LONG' | 'SHORT' | null — серверный флаг
  strategyType: string;
  strategySource?: 'builtin' | 'custom';
  customStrategyId?: string | null;
  customStrategyVersionId?: string | null;
  managedBy?: 'manual' | 'autopilot';
  autopilotProfileId?: string | null;
  autopilotSessionId?: string | null;
  hasUnreadExchangeError?: boolean;
  lastExchangeErrorAt?: string | null;
  lastExchangeErrorMessage?: string | null;
  showDebugBotStatsTools?: boolean;
}

export interface BotStat {
  _id: string;
  botId: string;
  tradeId?: string;
  event: string;
  direction: string;
  symbol: string;
  price: number;
  profit?: number;
  test?: boolean;
  timestamp: string;
  meta?: any;
}

/** Для графиков */
export type Candle = {
  time: number;   // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
};

export type TradePoint = {
  time: number;   // unix seconds
  price: number;
  side: 'LONG' | 'SHORT';
  action: 'ENTRY' | 'EXIT';
  qty?: number;
};

export type LiquidationMapZone = {
  id: string;
  side: 'buy-side' | 'sell-side';
  price: number;
  lowerPrice: number;
  upperPrice: number;
  startTime: number;
  endTime: number;
  touchCount: number;
  sweepCount: number;
  absorptionCount: number;
  score: number;
  intensity: number;
  active: boolean;
};

export type LiquidationMap = {
  timeframe: '4h';
  kind: 'synthetic';
  generatedAt: number;
  lookbackCandles: number;
  maxScore: number;
  zones: LiquidationMapZone[];
};

export type BotChartData = {
  symbol: string;
  candles: Candle[];
  trades: TradePoint[];
  interval: string;
  liquidationMap?: LiquidationMap;
};

export type MarginUsagePoint = {
  time: number;
  value: number;
  activeTrades: number;
};

export type MarginUsageChartData = {
  approximate: true;
  generatedAt: number;
  trackedBots: number;
  currentMarginUsd: number;
  maxMarginUsd: number;
  activeTrades: number;
  series: MarginUsagePoint[];
};

type ChartPayload = {
  botId: string;
  symbol: string;
  interval: string; // '1m' | '5m' | '15m' | '1h' и т.п.
  candles: Candle[];
  trades: TradePoint[];
  liquidationMap?: LiquidationMap;
};

/** ==== НОВОЕ: «живой» статус позиции по ботам (приходит по сокету position_update) ==== */
export type PositionLive = {
  symbol: string;
  direction: 'LONG' | 'SHORT';
  steps: number;
  totalQty: number;
  avgPrice: number;
  lastPrice: number;
  gross: number;
  openFees: number;
  estCloseFee: number;
  net: number;
  netPctOfEntryAmount: number;
  dayTargetUsd: number;
  updatedAt: number;
};

export type StrategyRuntimeDiagnostics = {
  kind: 'entry_diagnostics' | string;
  updatedAt: number;
  entryChecksTotal?: number;
  activeLevels?: number;
  confirmedLevels?: number;
  readyLevels?: number;
  signalsEmitted?: number;
  signalsWhileFlat?: number;
  signalsWhileOpen?: number;
  tradesOpened?: number;
  blockerCounts?: Record<string, number>;
  blockerRates?: Record<string, number>;
  topBlockerKey?: string | null;
  topBlockerCount?: number;
  topBlockerRatePct?: number;
};

interface BotsState {
  bots: Bot[];
  logs: Record<string, BotStat[]>;
  logsLoading: Record<string, boolean>;
  profits: Record<string, ProfitStats>;
  strategies: { value: string; label: string; description?: string }[];

  /** Графики */
  chartData: Record<string, BotChartData>;
  chartLoading: Record<string, boolean>;
  chartError: Record<string, string | undefined>;
  marginUsageChartData: MarginUsageChartData | null;
  marginUsageChartLoading: boolean;
  marginUsageChartError?: string;

  /** НОВОЕ: live-данные позиции */
  live: Record<string, PositionLive | undefined>;
  strategyStatus: Record<string, StrategyRuntimeDiagnostics | undefined>;
}

/** ====== INITIAL ====== */
const initialState: BotsState = {
  bots: [],
  logs: {},
  logsLoading: {},
  profits: {},
  strategies: [],

  chartData: {},
  chartLoading: {},
  chartError: {},
  marginUsageChartData: null,
  marginUsageChartLoading: false,
  marginUsageChartError: undefined,

  live: {}, // botId -> PositionLive
  strategyStatus: {},
};

/** ====== THUNKS ====== */

// Получение списка стратегий
export const fetchStrategies = createAsyncThunk(
  'bots/fetchStrategies',
  async (_, thunkAPI) => {
    const response = await axios.get('/strategies');
    return response.data as { value: string; label: string; description?: string }[];
  }
);

export const fetchBotStats = createAsyncThunk(
  'bots/fetchBotStats',
  async (botId: string, thunkAPI) => {
const response = await axios.get(`/bots/${botId}/stats`, {});
    return { botId, stats: response.data as BotStat[] };
  }
);

// ---- ОБНОВЛЕННЫЙ thunk! ----
export const createBot = createAsyncThunk(
  'bots/createBot',
  async (data: Omit<Bot, '_id' | 'status'>, thunkAPI) => {
const response = await axios.post('/bots', data, {});
    return response.data;
  }
);

export const startBot = createAsyncThunk(
  'bots/startBot',
  async (id: string, thunkAPI) => {
    try {
      await axios.post(`/bots/${id}/start`, {}, {});
      return id;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Cannot start bot';
      return thunkAPI.rejectWithValue(String(message));
    }
  }
);

export const stopBot = createAsyncThunk(
  'bots/stopBot',
  async (id: string, thunkAPI) => {
await axios.post(`/bots/${id}/stop`, {}, {});
    return id;
  }
);

export const deleteBot = createAsyncThunk(
  'bots/deleteBot',
  async (id: string, thunkAPI) => {
await axios.delete(`/bots/${id}`, {});
    return id;
  }
);

export const fetchBots = createAsyncThunk(
  'bots/fetchBots',
  async (_, thunkAPI) => {
const response = await axios.get('/bots', {});
    return response.data as Bot[];
  }
);

export const fetchAllBotsProfits = createAsyncThunk(
  'bots/fetchAllBotsProfits',
  async (_, thunkAPI) => {
const response = await axios.get('/bots/profits');
    return response.data; // { [botId]: { today, sinceStart, allTime } }
  }
);

/** === Данные для графика конкретного бота === */
export const fetchBotChartData = createAsyncThunk<
  ChartPayload,
  { botId: string; interval?: string; limit?: number }
>(
  'bots/fetchBotChartData',
  async ({ botId, interval = '1m', limit = 1000 }, thunkAPI) => {
    try {
const res = await axios.get(`/bots/${botId}/chart-data`, {params: { interval, limit },
      });
      return {
        botId,
        symbol: res.data.symbol,
        interval,
        candles: res.data.candles as Candle[],
        trades: res.data.trades as TradePoint[],
        liquidationMap: res.data.liquidationMap as LiquidationMap | undefined,
      };
    } catch (e: any) {
      return thunkAPI.rejectWithValue(
        e?.response?.data?.message ?? e?.message ?? 'Chart fetch failed'
      ) as any;
    }
  }
);

export const fetchMarginUsageChartData = createAsyncThunk<
  MarginUsageChartData,
  void
>(
  'bots/fetchMarginUsageChartData',
  async (_, thunkAPI) => {
    try {
      const res = await axios.get('/bots/margin-usage-chart');
      return res.data as MarginUsageChartData;
    } catch (e: any) {
      return thunkAPI.rejectWithValue(
        e?.response?.data?.message ?? e?.message ?? 'Margin usage chart fetch failed'
      ) as any;
    }
  }
);

/** ====== SLICE ====== */
const botsSlice = createSlice({
  name: 'bots',
  initialState,
  reducers: {
    updateProfitStats: (
      state,
      action: PayloadAction<{ botId: string; today: number; sinceStart: number; allTime: number }>
    ) => {
      const { botId, today, sinceStart, allTime } = action.payload;
      state.profits[botId] = { today, sinceStart, allTime };
    },

    /** ==== НОВОЕ: применение position_update в стор ==== */
    applyPositionUpdate: (
      state,
      action: PayloadAction<{
        botId: string | number;
        symbol: string;
        direction: 'LONG' | 'SHORT';
        steps: number;
        totalQty: number;
        avgPrice: number;
        lastPrice: number;
        gross: number;
        openFees: number;
        estCloseFee: number;
        net: number;
        netPctOfEntryAmount: number;
        dayTargetUsd: number;
        timestamp: number;
      }>
    ) => {
      const {
        botId, ...rest
      } = action.payload;

      state.live[String(botId)] = {
        symbol: rest.symbol,
        direction: rest.direction,
        steps: rest.steps,
        totalQty: rest.totalQty,
        avgPrice: rest.avgPrice,
        lastPrice: rest.lastPrice,
        gross: rest.gross,
        openFees: rest.openFees,
        estCloseFee: rest.estCloseFee,
        net: rest.net,
        netPctOfEntryAmount: rest.netPctOfEntryAmount,
        dayTargetUsd: rest.dayTargetUsd,
        updatedAt: rest.timestamp,
      };
    },

    /** (опционально) очистка live-данных по конкретному боту, например, после CLOSE */
    clearPositionLive: (
      state,
      action: PayloadAction<{ botId: string | number }>
    ) => {
      delete state.live[String(action.payload.botId)];
    },
    applyStrategyRuntimeStatus: (
      state,
      action: PayloadAction<{
        botId: string | number;
        status: StrategyRuntimeDiagnostics;
      }>
    ) => {
      state.strategyStatus[String(action.payload.botId)] = action.payload.status;
    },
    updateBotRuntimeErrorState: (
      state,
      action: PayloadAction<{
        botId: string | number;
        hasUnreadExchangeError: boolean;
        lastExchangeErrorAt?: string | null;
        lastExchangeErrorMessage?: string | null;
      }>
    ) => {
      const bot = state.bots.find((item) => item._id === String(action.payload.botId));
      if (!bot) return;
      bot.hasUnreadExchangeError = action.payload.hasUnreadExchangeError;
      if (action.payload.lastExchangeErrorAt !== undefined) {
        bot.lastExchangeErrorAt = action.payload.lastExchangeErrorAt;
      }
      if (action.payload.lastExchangeErrorMessage !== undefined) {
        bot.lastExchangeErrorMessage = action.payload.lastExchangeErrorMessage;
      }
    },
  },
  extraReducers: builder => {
    builder
      // CRUD ботов
      .addCase(createBot.fulfilled, (state, action) => {
        state.bots.push(action.payload);
      })
      .addCase(fetchBots.fulfilled, (state, action) => {
        state.bots = action.payload;
      })
      .addCase(startBot.fulfilled, (state, action) => {
        const id = action.payload;
        const bot = state.bots.find(bot => bot._id === id);
        if (bot) bot.status = 'running';
      })
      .addCase(stopBot.fulfilled, (state, action) => {
        const id = action.payload;
        const bot = state.bots.find(bot => bot._id === id);
        if (bot) bot.status = 'stopped';
        delete state.strategyStatus[id];
      })
      .addCase(deleteBot.fulfilled, (state, action) => {
        const id = action.payload;
        state.bots = state.bots.filter(bot => bot._id !== id);
        delete state.logs[id];
        delete state.logsLoading[id];
        delete state.chartData[id];
        delete state.chartLoading[id];
        delete state.chartError[id];
        delete state.live[id];
        delete state.strategyStatus[id];
      })

      // ЛОГИ/СТАТИСТИКА
      .addCase(fetchBotStats.pending, (state, action) => {
        const botId = action.meta.arg;
        state.logsLoading[botId] = true;
      })
      .addCase(fetchBotStats.fulfilled, (state, action) => {
        const { botId, stats } = action.payload;
        state.logs[botId] = stats;
        state.logsLoading[botId] = false;
        const bot = state.bots.find((item) => item._id === botId);
        if (bot) {
          bot.hasUnreadExchangeError = false;
        }
      })
      .addCase(fetchBotStats.rejected, (state, action) => {
        const botId = action.meta.arg;
        state.logsLoading[botId] = false;
      })

      // ПРОФИТЫ
      .addCase(fetchAllBotsProfits.fulfilled, (state, action) => {
        state.profits = action.payload || {};
      })

      // СТРАТЕГИИ
      .addCase(fetchStrategies.fulfilled, (state, action) => {
        state.strategies = action.payload;
      })

      // === ГРАФИКИ ===
      .addCase(fetchBotChartData.pending, (state, action) => {
        const botId = action.meta.arg.botId;
        state.chartLoading[botId] = true;
        state.chartError[botId] = undefined;
      })
      .addCase(fetchBotChartData.fulfilled, (state, action) => {
        const { botId, symbol, candles, trades, interval, liquidationMap } = action.payload;
        state.chartLoading[botId] = false;
        state.chartData[botId] = { symbol, candles, trades, interval, liquidationMap };
      })
      .addCase(fetchBotChartData.rejected, (state, action) => {
        const botId = action.meta.arg.botId;
        state.chartLoading[botId] = false;
        state.chartError[botId] = String(action.payload ?? action.error.message);
      })
      .addCase(fetchMarginUsageChartData.pending, (state) => {
        state.marginUsageChartLoading = true;
        state.marginUsageChartError = undefined;
      })
      .addCase(fetchMarginUsageChartData.fulfilled, (state, action) => {
        state.marginUsageChartLoading = false;
        state.marginUsageChartData = action.payload;
      })
      .addCase(fetchMarginUsageChartData.rejected, (state, action) => {
        state.marginUsageChartLoading = false;
        state.marginUsageChartError = String(action.payload ?? action.error.message);
      });
  }
});

export const {
  updateProfitStats,
  applyPositionUpdate,
  clearPositionLive,
  applyStrategyRuntimeStatus,
  updateBotRuntimeErrorState,
} = botsSlice.actions;

export default botsSlice.reducer;
